import axios from "axios";
import axiosRetry, { isNetworkOrIdempotentRequestError } from "axios-retry";
import { API_ENDPOINTS } from "../constants/API_ENDPOINTS";

// API configuration
const BASE_URL = process.env.REACT_APP_API_URL || "";
export const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "";

console.log("API Configuration:", {
  BASE_URL,
  FRONTEND_URL,
  NODE_ENV: process.env.NODE_ENV,
});

// Create axios instance with API URL
const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    Accept: "application/json",
  },
});

// Configure retry behavior
axiosRetry(api, {
  retries: 3,
  retryDelay: (retryCount) => {
    console.log(`Retry attempt ${retryCount}`);
    return axiosRetry.exponentialDelay(retryCount);
  },
  retryCondition: (error) => {
    console.log("Checking retry condition:", {
      error: error.message,
      status: error.response?.status,
      url: error.config?.url,
    });
    return Boolean(
      isNetworkOrIdempotentRequestError(error) ||
        error.code === "ECONNABORTED" ||
        error.message.includes("timeout") ||
        (error.response?.status &&
          error.response.status >= 500 &&
          error.response.status <= 599)
    );
  },
});

// Add request interceptor for logging and auth
api.interceptors.request.use(
  (config) => {
    // Try to get the token from localStorage directly
    let token = localStorage.getItem("token");

    // If not found, try to get it from the user object
    if (!token) {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          token = userData.token || userData.access_token;
        } catch (e) {
          console.error("Error parsing user data:", e);
          localStorage.removeItem("user");
        }
      }
    }

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Only set Accept header if not FormData
    if (!(config.data instanceof FormData)) {
      config.headers = config.headers || {};
      config.headers.Accept = "application/json";
    }

    console.log("Making request:", {
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
      headers: config.headers,
      data: config.data,
    });

    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log("Response received:", {
      status: response.status,
      url: response.config.url,
      data: response.data,
    });
    return response;
  },
  (error) => {
    console.error("Response error:", {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
    });

    // Handle specific error cases
    if (error.code === "ECONNABORTED") {
      throw new Error(
        "Request timed out. Please check your internet connection and try again."
      );
    }

    if (!error.response) {
      throw new Error(
        "Network error. Please check your internet connection and try again."
      );
    }

    // Handle 401 errors differently for login vs other endpoints
    if (error.response.status === 401) {
      const isLoginEndpoint = error.config.url === API_ENDPOINTS.LOGIN;
      const isVerifyEndpoint = error.config.url === API_ENDPOINTS.VERIFY_TOKEN;

      // Don't redirect or show session expired for login/verify endpoints
      if (!isLoginEndpoint && !isVerifyEndpoint) {
        localStorage.removeItem("user");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
          throw new Error("Session expired. Please login again.");
        }
      }
    }

    // Prefer the server's detail for 5xx when present
    if (error.response.status >= 500) {
      const detail = error.response.data?.detail;
      throw new Error(
        typeof detail === "string" && detail
          ? detail
          : "Server error. Please try again later."
      );
    }

    // Return the error response data if available
    if (error.response.data?.detail) {
      throw new Error(error.response.data.detail);
    }

    return Promise.reject(error);
  }
);

// Type definitions
export interface LoginData {
  username: string;
  password: string;
}

export interface RegisterData extends LoginData {
  email: string;
}

// Auth API methods
export const authApi = {
  login: async (data: LoginData) => {
    try {
      console.log("Attempting login with username:", data.username);
      const response = await api.post(API_ENDPOINTS.LOGIN, data);
      console.log("Login response:", response.data);
      if (response.data.access_token) {
        const userData = {
          id: response.data.user_id,
          token: response.data.access_token,
          userId: response.data.user_id,
          username: data.username,
          role: response.data.role || "user",
          email: response.data.email || "",
        };
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("token", response.data.access_token);
      }
      return response.data;
    } catch (error: any) {
      console.error("Login error:", {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        data: error.response?.data,
      });
      if (error.response?.status === 401) {
        throw new Error(
          error.response.data.detail || "Incorrect username or password"
        );
      }
      throw error;
    }
  },

  register: async (data: RegisterData) => {
    try {
      console.log("Attempting registration with username:", data.username);
      const response = await api.post(API_ENDPOINTS.REGISTER, data);
      console.log("Registration response:", response.data);
      if (response.data.access_token) {
        const userData = {
          id: response.data.user_id,
          token: response.data.access_token,
          userId: response.data.user_id,
          username: data.username,
          role: response.data.role || "user",
          email: data.email || "",
        };
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("token", response.data.access_token);
      }
      return response.data;
    } catch (error: any) {
      console.error("Registration error:", {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message,
        data: error.response?.data,
      });
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem("user");
  },

  checkAuth: async () => {
    try {
      const response = await api.get(API_ENDPOINTS.VERIFY_TOKEN);
      return response.data;
    } catch (error: any) {
      console.error("Auth check error:", error);
      // Only remove user data if it's an auth error
      if (error.response?.status === 401) {
        localStorage.removeItem("user");
      }
      return null;
    }
  },
};

export { API_ENDPOINTS };
export default api;
